import type { ApiError as ApiErrorBody } from '@pulsepoint/shared';

/** A failed API call: the message to show, and per-field problems for forms. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly fields: Record<string, string> = {},
  ) {
    super(message);
  }
}

/** The page can read this cookie; it exists only while there is a sign-in to use. */
export const hasSession = () => csrfToken() !== '';

function csrfToken(): string {
  const m = /(?:^|;\s*)pp_csrf=([^;]+)/.exec(document.cookie);
  return m ? decodeURIComponent(m[1]!) : '';
}

/** Called when the sign-in has ended for good (the app then shows the sign-in page). */
let onSignedOut: () => void = () => undefined;
export function setSignedOutHandler(fn: () => void) {
  onSignedOut = fn;
}

// Several calls can fail at once when the 15-minute token runs out; they share one refresh.
let refreshing: Promise<boolean> | null = null;
function refreshOnce(): Promise<boolean> {
  if (!hasSession()) return Promise.resolve(false);
  refreshing ??= fetch('/api/auth/refresh', { method: 'POST', credentials: 'same-origin', headers: { 'X-CSRF-Token': csrfToken() } })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => {
      // Calls already waiting hold this promise; anything later starts a fresh renewal.
      refreshing = null;
    });
  return refreshing;
}

const NO_RETRY = new Set(['/auth/login', '/auth/refresh', '/auth/logout', '/auth/forgot-password', '/auth/reset-password']);

export async function api<T = unknown>(method: string, path: string, body?: unknown, retried = false): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (method !== 'GET') headers['X-CSRF-Token'] = csrfToken();

  let res: Response;
  try {
    res = await fetch(`/api${path}`, { method, headers, credentials: 'same-origin', body: body === undefined ? undefined : JSON.stringify(body) });
  } catch {
    throw new ApiError(0, "Can't reach Pulsepoint. Check your connection and try again.");
  }

  if (res.status === 401 && !retried && !NO_RETRY.has(path)) {
    if (await refreshOnce()) return api<T>(method, path, body, true);
    onSignedOut();
  }
  if (res.status === 204) return undefined as T;

  const data = (await res.json().catch(() => null)) as unknown;
  if (!res.ok) {
    const e = (data ?? {}) as Partial<ApiErrorBody>;
    throw new ApiError(res.status, e.error ?? `Something went wrong (${res.status}). Please try again, or contact our support team at support@infinityev.com.au`, e.fields ?? {});
  }
  return data as T;
}

export const get = <T>(path: string) => api<T>('GET', path);
export const post = <T>(path: string, body?: unknown) => api<T>('POST', path, body ?? {});
export const patch = <T>(path: string, body: unknown) => api<T>('PATCH', path, body);
export const put = <T>(path: string, body: unknown) => api<T>('PUT', path, body);
export const del = <T>(path: string) => api<T>('DELETE', path);

/**
 * A file from the API (a PDF, a zip), saved by the browser under the name the API gives it.
 * Resolves with that name.
 */
export async function downloadFile(path: string, body: unknown, retried = false): Promise<string> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() }, credentials: 'same-origin', body: JSON.stringify(body) });
  } catch {
    throw new ApiError(0, "Can't reach Pulsepoint. Check your connection and try again.");
  }
  if (res.status === 401 && !retried) {
    if (await refreshOnce()) return downloadFile(path, body, true);
    onSignedOut();
  }
  if (!res.ok) {
    const e = ((await res.json().catch(() => null)) ?? {}) as Partial<ApiErrorBody>;
    throw new ApiError(res.status, e.error ?? `Something went wrong (${res.status}). Please try again, or contact our support team at support@infinityev.com.au`, e.fields ?? {});
  }
  const name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1] ?? 'download';
  const url = URL.createObjectURL(await res.blob());
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return name;
}
